import calculateMidColor from '@utilities/colors';
import numeral from 'numeral';
import { merge, isNil } from 'lodash';
import { toSignificant } from '@/utilities/formatting';

const mobileScale = 0.66;
export const REGULAR_FONT_SIZE = '16px';
export const XSMALL_FONT_SIZE = '12px';

export function defaultBasicAreaOptions(HighCharts, currency?: any) {
  const currencySymbol = (currency && currency.reportingCurrencySymbol) || '';
  const reportingUnit = (currency && currency.reportingUnit) || '';
  return {
    chart: {
      renderTo: 'container',
      type: 'areaspline',
      spacingLeft: 0,
      marginLeft: 72 * (typeof mobileScale === 'undefined' ? 1 : mobileScale),
    },
    legend: {
      enabled: false,
    },
    title: {
      text: '',
    },
    yAxis: {
      enabled: true,
      showFirstLabel: true,
      showLastLabel: true,
      labels: {
        enabled: true,
        formatter() {
          if (
            ((this as any).isFirst || (this as any).isLast) &&
            currencySymbol &&
            reportingUnit
          ) {
            return (
              currencySymbol +
              numeral((this as any).value * reportingUnit).format('0a')
            );
          }
        },
      },
      tickWidth: 0,
      alternateGridColor: 'rgba(33,47,61,0.2)',
      gridLineWidth: 0,
      title: {
        text: '',
      },
      plotLines: [
        {
          color: 'white',
          width: 1,
          value: 0,
        },
      ],
    },
    xAxis: {
      tickInterval: 1,
      tickmarkPlacement: 'on',
      startOnTick: true,
      endOnTick: true,
      minPadding: 0,
      maxPadding: 0,
    },
    plotOptions: {
      areaspline: {
        dataLabels: {
          style: {
            fontSize: XSMALL_FONT_SIZE,
          },
        },
        fillOpacity: 0.5,
        pointPlacement: 0,
        negativeColor: '#FF5050',
        negativefillColor: '#FF5050',
        style: {
          color:
            (HighCharts.theme && HighCharts.theme.contrastTextColor) || 'white',
          fontSize: XSMALL_FONT_SIZE,
        },
        marker: {
          enabled: false,
          symbol: 'circle',
          radius: 2,
          states: {
            hover: {
              enabled: true,
            },
          },
        },
        events: {
          legendItemClick: function () {
            return true;
          },
        },
      },
    },
  };
}

export function generatePlotBandsV2(
  min,
  max,
  bands,
  value,
  colStart = '#55BF3B',
  colEnd = '#DF5353',
  bonusMax?: number
) {
  const bandData: {
    from: number;
    to: number;
    innerRadius: string;
    outerRadius: string;
    color: string;
  }[] = [];

  if (min < 0) {
    bandData.push({
      from: min,
      to: 0,
      innerRadius: '70%',
      outerRadius: '100%',
      color: '#863232', // negative zone
    });
  }

  for (let i = 0; i < bands; i++) {
    bandData.push({
      from: i * (max / bands),
      to: (i + 1) * (max / bands),
      innerRadius: '70%',
      outerRadius: '100%',
      color: calculateMidColor(colStart, colEnd, i / (bands - 1), 0.3), // green
    });
  }

  if (typeof bonusMax !== 'undefined') {
    //extra bonus section if needed
    bandData.push({
      from: max,
      to: bonusMax,
      innerRadius: '70%',
      outerRadius: '100%',
      color: 'rgba(97, 218, 67, 0.3)', // green
    });
  }

  if (typeof value !== 'undefined' && value > 0) {
    // plot 100% opacity line for value
    for (let i = 0; i < bands; i++) {
      if ((i + 1) * (max / bands) > value) {
        bandData.push({
          from: i * (max / bands),
          to: value,
          innerRadius: '70%',
          outerRadius: '100%',
          color: calculateMidColor(colStart, colEnd, value / max), // green
        });
        break;
      } else {
        bandData.push({
          from: i * (max / bands),
          to: (i + 1) * (max / bands),
          innerRadius: '70%',
          outerRadius: '100%',
          color: calculateMidColor(colStart, colEnd, i / (bands - 1)), // green
        });
      }
    }
  } else if (typeof value !== 'undefined' && value < 0 && value > min) {
    // plot negative part
    bandData.push({
      from: 0,
      to: Math.max(value, min),
      innerRadius: '80%',
      outerRadius: '90%',
      color: 'rgba(255,255,255,0.4)', // white
    });
  } else if (typeof value !== 'undefined' && value < min) {
    // off the scale!
    bandData.push({
      from: 0,
      to: min,
      innerRadius: '80%',
      outerRadius: '90%',
      color: 'rgba(255,255,255,0.4)', // white
    });
  }

  if (typeof bonusMax !== 'undefined' && value > max && value <= bonusMax) {
    //extra bonus section if needed
    bandData.push({
      from: max,
      to: Math.min(value, bonusMax),
      innerRadius: '70%',
      outerRadius: '100%',
      color: 'rgba(97, 218, 67, 1)', // green
    });
  } else if (typeof bonusMax !== 'undefined' && value > bonusMax) {
    bandData.push({
      from: max,
      to: bonusMax,
      innerRadius: '70%',
      outerRadius: '100%',
      color: 'rgba(97, 218, 67, 1)', // green
    });
  }

  return bandData;
}

function defaultValueGaugeChartOptions() {
  return {
    title: { style: { color: 'white' } },
    subtitle: { style: { color: 'white' } },
    chart: {
      type: 'gauge',
      backgroundColor: 'transparent',
      plotBackgroundColor: null,
      plotBackgroundImage: null,
      plotBorderWidth: 0,
      plotShadow: false,
      marginBottom: -50,
      marginTop: 30 * 1, //instead of returning 1 return mobile scale value such as 0.6; example: (mobileScale ? mobileScale : 1)
      marginLeft: 30,
      marginRight: 30,
      height: 200,
    },
    legend: {
      itemStyle: { color: 'white' },
      enabled: true,
    },
    tooltip: {
      enabled: true,
    },
    pane: {
      startAngle: -70,
      endAngle: 70,
      borderWidth: 0,
      size: '110%',
      background: [
        {
          backgroundColor: '#262E3A',
          borderWidth: 0,
        },
      ],
    },

    // the value axis
    yAxis: {
      min: 0,
      max: 50,
      lineWidth: 0,
      minorTickInterval: null,
      tickWidth: 0,
      labels: {
        style: {
          color: 'white',
        },
        enabled: false,
      },
      title: { style: { color: 'white' } },
    },

    plotOptions: {
      gauge: {
        dial: {
          radius: '80%',
          backgroundColor: '#2F80ED',
          borderColor: '#2F80ED',
          borderWidth: 0,
          baseWidth: 10,
          topWidth: 1,
          baseLength: '70%', // of radius
          rearLength: '0',
        },
        showInLegend: true,
      },
      pivot: {
        backgroundColor: '#2F80ED',
      },
    },
    credits: { enabled: false },
  };
}

export function defaultGaugeOptions() {
  return {
    chart: {
      type: 'gauge',
      plotBackgroundColor: null,
      plotBackgroundImage: null,
      plotBorderWidth: 0,
      plotShadow: false,
      spacingTop: 15,
    },
    legend: {
      enabled: true,
    },
    tooltip: {
      enabled: true,
    },
    pane: {
      startAngle: -150,
      endAngle: 150,
      borderWidth: 0,
      size: '110%',
      background: [
        {
          //backgroundColor: '#45505B',
          backgroundColor: 'transparent',
          borderWidth: 0,
        },
      ],
    },
    // the value axis
    yAxis: {
      min: -10,
      max: 50,
      lineWidth: 0,
      minorTickInterval: null,
      tickWidth: 0,
      labels: {
        enabled: false,
      },
    },

    plotOptions: {
      gauge: {
        dial: {
          radius: '80%',
          backgroundColor: '#2F80ED',
          borderColor: '#2F80ED',
          borderWidth: 0,
          baseWidth: 10,
          topWidth: 1,
          baseLength: '70%', // of radius
          rearLength: '0',
        },
        showInLegend: true,
        pivot: {
          backgroundColor: '#2F80ED',
        },
      },
    },
  };
}

export function defaultSplineOptions() {
  // Simple Line
  return {
    chart: {
      renderTo: 'container',
      type: 'spline',
    },
    legend: {
      enabled: false,
    },
    title: {
      text: '',
    },
    tooltip: {
      valueSuffix: '%',
    },
    yAxis: {
      enabled: true,
      showFirstLabel: true,
      showLastLabel: true,
      labels: {
        enabled: true,
        formatter() {
          if ((this as any).isFirst === true || (this as any).isLast === true) {
            return (this as any).value + '%';
          }
        },
      },
      tickWidth: 0,
      gridLineWidth: 0,
      min: 0,
      title: {
        text: '',
      },
    },
    xAxis: {
      reversed: true,
      tickInterval: 1,
      tickmarkPlacement: 'on',
      minPadding: 0,
      maxPadding: 0,
      categories: [
        'Now',
        '-1 yr',
        '-2 yrs',
        '-3 yrs',
        '-4 yrs',
        '-5 yrs',
        '-6 yrs',
        '-7 yrs',
        '-8 yrs',
        '-9 yrs',
        '-10 yrs',
      ],
      /*labels: {
				enabled:true
				}
			}*/
    },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.5,
        pointPlacement: 0,
        marker: {
          enabled: false,
          symbol: 'circle',
          radius: 2,
          states: {
            hover: {
              enabled: true,
            },
          },
        },
        dataLabels: {
          enabled: true,
          align: 'center',
          verticalAlign: 'bottom',
          formatter() {
            return (this as any).series.name;
          },
        },
      },
    },
  };
}

function _getGaugeChartTooltip(
  type,
  seriesName,
  seriesValue,
  industryDesc,
  industryValue,
  marketDesc,
  marketValue?: number,
  factorSymbol?: string
) {
  const industryToolTip =
    industryValue && industryValue !== null
      ? `<br /> ${
          industryDesc ? industryDesc : 'Industry'
        } ${type}: ${toSignificant(industryValue, 1)}${factorSymbol}`
      : '';
  const marketToolTip =
    marketValue && marketValue !== null
      ? `<br /> ${marketDesc ? marketDesc : 'Market'} ${type}: ${toSignificant(
          marketValue,
          1
        )}${factorSymbol}`
      : '';
  const tooltipText = `${seriesName} ${type}: ${toSignificant(
    seriesValue,
    1
  )}${factorSymbol} ${industryToolTip} ${marketToolTip}`;

  return tooltipText;
}

export function PBOptions(propOptions) {
  let titleText = '';
  const bands = [0, 10, 20];
  const bandData = generatePlotBandsV2(0, 20, 10, propOptions.priceBook);
  if (propOptions.priceBook) {
    titleText =
      '<span style="font-size:50%;">PB </span>' +
      toSignificant(propOptions.priceBook, 1) +
      '<span style="font-size:50%;">x</span>';
  } else {
    titleText =
      '<span style="font-size:50%;">PB </span>-<span style="font-size:50%;">x</span>';
  }
  // PB GAUGE
  return merge({}, defaultValueGaugeChartOptions(), {
    tooltip: {
      useHTML: true,
      formatter: function () {
        const tooltipText = _getGaugeChartTooltip(
          'PB',
          propOptions.companyName,
          propOptions.priceBook,
          propOptions.industryDescription,
          propOptions.industryPriceBook,
          propOptions.marketDescription,
          propOptions.marketPriceBook,
          'x'
        );

        return tooltipText;
      },
    },
    yAxis: {
      min: 0,
      max: 20,
      tickInterval: 1.25,
      title: {
        useHTML: true,
        text: titleText,
        y: 70,
        style: {
          fontFamily: '"Roboto"',
          fontWeight: '900',
          fontSize: '2.1em',
          zIndex: 999,
        },
      },
      labels: {
        enabled: true,
        rotation: 'auto',
        align: 'center',
        distance: 5,
        x: 0,
        y: 0,
        formatter: function () {
          if (bands.indexOf((this as any).value) !== -1) {
            return (this as any).value + 'x';
          }
        },
      },
      plotBands: bandData,
    },
    series: [
      {
        dataLabels: {
          enabled: false,
        },
        name: propOptions.tickerSymbol
          ? propOptions.tickerSymbol
          : propOptions.companyName,
        data: [propOptions.priceBook],
        overshoot: 3,
        color: '#2F80ED',
        marker: {
          radius: 8,
        },
        index: 3,
        legendIndex: 0,
        pivot: {
          backgroundColor: '#2F80ED',
        },
      },
      {
        name: !isNil(propOptions.industryDescription)
          ? propOptions.industryDescription
          : 'Industry',
        data: [propOptions.industryPriceBook],
        visible: !isNil(propOptions.industryPriceBook),
        showInLegend: !isNil(propOptions.industryPriceBook),
        dataLabels: {
          enabled: false,
        },
        overshoot: 3,
        dial: {
          backgroundColor: '#F0A999',
          baseLength: '65%', // of radius
          radius: '70%',
          baseWidth: 6,
        },
        color: '#F0A999',
        marker: {
          radius: 8,
          symbol: 'circle',
        },
        index: 2,
        legendIndex: 1,
      },
      {
        name: !isNil(propOptions.marketDescription)
          ? propOptions.marketDescription
          : 'Market',
        data: [propOptions.marketPriceBook],
        visible: !isNil(propOptions.marketPriceBook),
        showInLegend: !isNil(propOptions.marketPriceBook),
        dataLabels: {
          enabled: false,
        },
        overshoot: 3,
        dial: {
          backgroundColor: '#ABD588',
          baseLength: '65%',
          radius: '70%',
          baseWidth: 6,
        },
        color: '#ABD588',
        marker: {
          radius: 8,
          symbol: 'circle',
        },
        index: 1,
        legendIndex: 2,
      },
    ],
    plotOptions: {
      gauge: {
        events: {
          legendItemClick: function (options) {
            const legend =
              (options && options.target && options.target.name) || '';
            const status = options && options.target && !options.target.visible;
            if (propOptions.analyticsHandler)
              propOptions.analyticsHandler('PBChart', legend, status);
          },
        },
      },
    },
  });
}

export function PEGOptions(propOptions) {
  let titleText = '';
  const bands = [0, 2, 4];
  const bandData = generatePlotBandsV2(
    0,
    4,
    10,
    propOptions.priceExpectedGrowth
  );

  if (propOptions.priceExpectedGrowth) {
    titleText =
      '<span style="font-size:50%;">PEG </span>' +
      toSignificant(propOptions.priceExpectedGrowth, 1) +
      '<span style="font-size:50%;">x</span>';
  } else {
    titleText =
      '<span style="font-size:50%;">PEG </span>-<span style="font-size:50%;">x</span>';
  }
  return merge({}, defaultValueGaugeChartOptions(), {
    tooltip: {
      useHTML: true,
      formatter: function () {
        const tooltipText = _getGaugeChartTooltip(
          'PEG',
          propOptions.companyName,
          propOptions.priceExpectedGrowth,
          propOptions.industryDescription,
          propOptions.industryPriceExpectedGrowth,
          propOptions.marketDescription,
          propOptions.marketPriceExpectedGrowth,
          'x'
        );

        return tooltipText;
      },
    },
    yAxis: {
      min: 0,
      max: 4,
      tickInterval: 0.25,
      title: {
        useHTML: true,
        text: titleText,
        y: 70,
        style: {
          fontFamily: '"Roboto"',
          fontWeight: '900',
          fontSize: '2.1em',
          zIndex: 999,
        },
      },
      labels: {
        enabled: true,
        rotation: 'auto',
        align: 'center',
        distance: 5,
        x: 0,
        y: 0,
        formatter: function () {
          if (bands.indexOf((this as any).value) !== -1) {
            return (this as any).value + 'x';
          }
        },
      },
      plotBands: bandData,
    },
    series: [
      {
        dataLabels: {
          enabled: false,
        },
        name: propOptions.tickerSymbol
          ? propOptions.tickerSymbol
          : propOptions.companyName,
        data: [propOptions.priceExpectedGrowth],
        overshoot: 3,
        color: '#2F80ED',
        marker: {
          radius: 8,
        },
        index: 3,
        legendIndex: 0,
        pivot: {
          backgroundColor: '#2F80ED',
        },
      },
      {
        name: !isNil(propOptions.industryDescription)
          ? propOptions.industryDescription
          : 'Industry',
        data: [propOptions.industryPriceExpectedGrowth],
        visible: !isNil(propOptions.industryPriceExpectedGrowth),
        showInLegend: !isNil(propOptions.industryPriceExpectedGrowth),
        dataLabels: {
          enabled: false,
        },
        overshoot: 3,
        dial: {
          backgroundColor: '#F0A999',
          baseLength: '65%', // of radius
          radius: '70%',
          baseWidth: 6,
        },
        color: '#F0A999',
        marker: {
          radius: 8,
          symbol: 'circle',
        },
        index: 2,
        legendIndex: 1,
      },
      {
        name: !isNil(propOptions.marketDescription)
          ? propOptions.marketDescription
          : 'Market',
        data: [propOptions.marketPriceExpectedGrowth],
        visible: !isNil(propOptions.marketPriceExpectedGrowth),
        showInLegend: !isNil(propOptions.marketPriceExpectedGrowth),
        dataLabels: {
          enabled: false,
        },
        overshoot: 3,
        dial: {
          backgroundColor: '#ABD588',
          baseLength: '65%',
          radius: '70%',
          baseWidth: 6,
        },
        color: '#ABD588',
        marker: {
          radius: 8,
          symbol: 'circle',
        },
        index: 1,
        legendIndex: 2,
      },
    ],
    plotOptions: {
      gauge: {
        events: {
          legendItemClick: function (options) {
            const legend =
              (options && options.target && options.target.name) || '';
            const status = options && options.target && !options.target.visible;
            if (propOptions.analyticsHandler)
              propOptions.analyticsHandler('PEGChart', legend, status);
          },
        },
      },
    },
  });
}

export function PEOptions(propOptions) {
  let titleText = '';
  const bands = [0, 30, 60];
  const bandData = generatePlotBandsV2(0, 60, 10, propOptions.priceEquity);

  if (propOptions.priceEquity) {
    titleText =
      '<span style="font-size:50%;">PE </span>' +
      toSignificant(propOptions.priceEquity, 1) +
      '<span style="font-size:50%;">x</span>';
  } else {
    titleText =
      '<span style="font-size:50%;">PE </span>-<span style="font-size:50%;">x</span>';
  }
  return merge({}, defaultValueGaugeChartOptions(), {
    tooltip: {
      useHTML: true,
      formatter: function () {
        const tooltipText = _getGaugeChartTooltip(
          'PE',
          propOptions.companyName,
          propOptions.priceEquity,
          propOptions.industryDescription,
          propOptions.industryPriceEquity,
          propOptions.marketDescription,
          propOptions.marketPriceEquity,
          'x'
        );

        return tooltipText;
      },
    },
    yAxis: {
      min: 0,
      max: 60,
      tickInterval: 1.25,
      title: {
        useHTML: true,
        text: titleText,
        y: 70,
        style: {
          fontFamily: '"Roboto"',
          fontWeight: '900',
          fontSize: '2.1em',
          zIndex: 999,
        },
      },
      labels: {
        enabled: true,
        rotation: 'auto',
        align: 'center',
        distance: 5,
        x: 0,
        y: 0,
        formatter: function () {
          if (bands.indexOf((this as any).value) !== -1) {
            return (this as any).value + 'x';
          }
        },
      },
      plotBands: bandData,
    },
    series: [
      {
        name: propOptions.tickerSymbol
          ? propOptions.tickerSymbol
          : propOptions.companyName,
        data: [propOptions.priceEquity],
        dataLabels: {
          enabled: false,
        },
        overshoot: 3,
        color: '#2F80ED',
        marker: {
          radius: 8,
          symbol: 'circle',
        },
        index: 3,
        legendIndex: 0,
        pivot: {
          backgroundColor: '#2F80ED',
        },
      },
      {
        name: !isNil(propOptions.industryDescription)
          ? propOptions.industryDescription
          : 'Industry',
        data: [propOptions.industryPriceEquity],
        visible: !isNil(propOptions.industryPriceEquity),
        showInLegend: !isNil(propOptions.industryPriceEquity),
        dataLabels: {
          enabled: false,
        },
        overshoot: 3,
        dial: {
          backgroundColor: '#F0A999',
          baseLength: '65%', // of radius
          radius: '70%',
          baseWidth: 6,
        },
        color: '#F0A999',
        marker: {
          radius: 8,
          symbol: 'circle',
        },
        index: 2,
        legendIndex: 1,
      },
      {
        name: !isNil(propOptions.marketDescription)
          ? propOptions.marketDescription
          : 'Market',
        visible: !isNil(propOptions.marketPriceEquity),
        showInLegend: !isNil(propOptions.marketPriceEquity),
        data: propOptions.marketPriceEquity
          ? [propOptions.marketPriceEquity]
          : null,
        dataLabels: {
          enabled: false,
        },
        overshoot: 3,
        dial: {
          backgroundColor: '#ABD588',
          baseLength: '65%', // of radius
          radius: '70%',
          baseWidth: 6,
        },
        color: '#ABD588',
        marker: {
          radius: 8,
          symbol: 'circle',
        },
        index: 0,
        legendIndex: 2,
      },
    ],
    plotOptions: {
      gauge: {
        events: {
          legendItemClick: function (options) {
            const legend =
              (options && options.target && options.target.name) || '';
            const status = options && options.target && !options.target.visible;
            if (propOptions.analyticsHandler)
              propOptions.analyticsHandler('PEChart', legend, status);
          },
        },
      },
    },
  });
}

export function returnOnEquityOptions(propOptions) {
  let {
    ROE,
    industryROE,
    marketROE,
    companyName,
    industryDescription,
    marketDescription,
  } = propOptions;
  ROE = +toSignificant(ROE, 2);

  const bands = [0, 20];
  const plotBands = generatePlotBandsV2(
    -8,
    32,
    10,
    ROE,
    '#DF5353',
    '#55BF3B',
    40
  );

  return merge({}, defaultGaugeOptions(), {
    chart: {
      height: 250,
      marginTop: 20,
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        const tooltipText = _getGaugeChartTooltip(
          'ROE',
          companyName,
          ROE,
          industryDescription,
          industryROE,
          marketDescription,
          marketROE,
          '%'
        );

        return tooltipText;
      },
    },
    yAxis: {
      min: -8,
      max: 40,
      title: {
        useHTML: true,
        text:
          '<span style="font-size:150%;">' +
          ROE +
          '</span>%<br>Return<br>on Equity',
        align: 'middle',
        y: 70,
        style: {
          fontSize: '1.143em',
          textAlign: 'center',
          zIndex: 2,
        },
      },
      labels: {
        enabled: true,
        rotation: 'auto',
        align: 'center',
        distance: 5,
        x: 0,
        y: 0,
        formatter: function () {
          if (bands.indexOf((this as any).value) !== -1) {
            return (this as any).value + '%';
          }
        },
      },
      plotBands: plotBands,
    },
    series: [
      {
        dataLabels: {
          enabled: false,
        },
        name: companyName,
        data: [ROE],
        overshoot: 5,
        color: '#2F80ED',
        marker: {
          radius: 8,
        },
        index: 3,
        legendIndex: 0,
        pivot: {
          backgroundColor: '#2F80ED',
        },
      },
      {
        name: !isNil(industryDescription) ? industryDescription : 'Industry',
        data: [industryROE],
        visible: !isNil(industryROE),
        showInLegend: !isNil(industryROE),
        dataLabels: {
          enabled: false,
        },
        overshoot: 5,
        dial: {
          backgroundColor: '#F0A999',
          baseLength: '65%', // of radius
          radius: '70%',
          baseWidth: 6,
        },
        color: '#F0A999',
        marker: {
          radius: 8,
          symbol: 'circle',
        },
        index: 2,
        legendIndex: 1,
      },
      {
        name: !isNil(marketDescription) ? marketDescription : 'Market',
        data: [marketROE],
        visible: !isNil(marketROE),
        showInLegend: !isNil(marketROE),
        dataLabels: {
          enabled: false,
        },
        overshoot: 5,
        dial: {
          backgroundColor: '#ABD588',
          baseLength: '65%',
          radius: '70%',
          baseWidth: 6,
        },
        color: '#ABD588',
        marker: {
          radius: 8,
          symbol: 'circle',
        },
        index: 1,
        legendIndex: 2,
      },
    ],
  });
}

export function returnOnAssetsOptions(propOptions) {
  let {
    ROA,
    industryROA,
    marketROA,
    companyName,
    industryDescription,
    marketDescription,
  } = propOptions;
  ROA = +toSignificant(ROA, 2);

  const bands = [0, 5];
  const plotBands = generatePlotBandsV2(
    -2,
    8,
    10,
    ROA,
    '#DF5353',
    '#55BF3B',
    10
  );

  return merge({}, defaultGaugeOptions(), {
    chart: {
      height: 250,
      marginTop: 20,
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        const tooltipText = _getGaugeChartTooltip(
          'ROA',
          companyName,
          ROA,
          industryDescription,
          industryROA,
          marketDescription,
          marketROA,
          '%'
        );

        return tooltipText;
      },
    },
    yAxis: {
      min: -2,
      max: 10,
      title: {
        useHTML: true,
        text:
          '<span style="font-size:150%;">' +
          ROA +
          '</span>%<br>Return<br>on Assets',
        align: 'middle',
        y: 70,
        style: {
          fontSize: '1.143em',
          textAlign: 'center',
          zIndex: 2,
        },
      },
      labels: {
        enabled: true,
        rotation: 'auto',
        align: 'center',
        distance: 5,
        x: 0,
        y: 0,
        formatter: function () {
          if (bands.indexOf((this as any).value) !== -1) {
            return (this as any).value + '%';
          }
        },
      },
      plotBands: plotBands,
    },
    series: [
      {
        dataLabels: {
          enabled: false,
        },
        name: companyName,
        data: [ROA],
        overshoot: 5,
        color: '#2F80ED',
        marker: {
          radius: 8,
        },
        index: 3,
        legendIndex: 0,
        pivot: {
          backgroundColor: '#2F80ED',
        },
      },
      {
        name: !isNil(industryDescription) ? industryDescription : 'Industry',
        data: [industryROA],
        visible: !isNil(industryROA),
        showInLegend: !isNil(industryROA),
        dataLabels: {
          enabled: false,
        },
        overshoot: 5,
        dial: {
          backgroundColor: '#F0A999',
          baseLength: '65%', // of radius
          radius: '70%',
          baseWidth: 6,
        },
        color: '#F0A999',
        marker: {
          radius: 8,
          symbol: 'circle',
        },
        index: 2,
        legendIndex: 1,
      },
      {
        name: !isNil(marketDescription) ? marketDescription : 'Market',
        data: [marketROA],
        visible: !isNil(marketROA),
        showInLegend: !isNil(marketROA),
        dataLabels: {
          enabled: false,
        },
        overshoot: 5,
        dial: {
          backgroundColor: '#ABD588',
          baseLength: '65%',
          radius: '70%',
          baseWidth: 6,
        },
        color: '#ABD588',
        marker: {
          radius: 8,
          symbol: 'circle',
        },
        index: 1,
        legendIndex: 2,
      },
    ],
  });
}

export function returnOnCapitalOptions(propOptions) {
  let { ROCE, industryROCE, companyName, industryDescription } = propOptions;
  ROCE = +toSignificant(ROCE, 2);

  const bands = [0, 20];
  const plotBands = generatePlotBandsV2(
    -10,
    40,
    10,
    ROCE,
    '#DF5353',
    '#55BF3B',
    40
  );

  return merge({}, defaultGaugeOptions(), {
    chart: {
      height: 250,
      marginTop: 20,
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        const tooltipText = _getGaugeChartTooltip(
          'ROCE',
          companyName,
          ROCE,
          industryDescription,
          industryROCE,
          '%'
        );

        return tooltipText;
      },
    },
    yAxis: {
      min: -10,
      max: 40,
      title: {
        useHTML: true,
        text:
          '<span style="font-size:150%;">' +
          ROCE +
          '</span>%<br>Return<br>on Capital',
        align: 'middle',
        y: 70,
        style: {
          fontSize: '1.143em',
          textAlign: 'center',
          zIndex: 2,
        },
      },
      labels: {
        enabled: true,
        rotation: 'auto',
        align: 'center',
        distance: 5,
        x: 0,
        y: 0,
        formatter() {
          if (bands.indexOf((this as any).value) !== -1) {
            return (this as any).value + '%';
          }
        },
      },
      plotBands: plotBands,
    },
    series: [
      {
        dataLabels: {
          enabled: false,
        },
        name: companyName,
        data: [ROCE],
        overshoot: 5,
        color: '#2F80ED',
        marker: {
          radius: 8,
        },
        index: 3,
        legendIndex: 0,
        pivot: {
          backgroundColor: '#2F80ED',
        },
      },
      {
        name: !isNil(industryDescription) ? industryDescription : 'Industry',
        data: [industryROCE],
        visible: !isNil(industryROCE),
        showInLegend: !isNil(industryROCE),
        dataLabels: {
          enabled: false,
        },
        overshoot: 5,
        dial: {
          backgroundColor: '#F0A999',
          baseLength: '65%', // of radius
          radius: '70%',
          baseWidth: 6,
        },
        color: '#F0A999',
        marker: {
          radius: 8,
          symbol: 'circle',
        },
        index: 2,
        legendIndex: 1,
      },
    ],
  });
}

export function defaultPieChartOptions(Highcharts) {
  return {
    chart: {
      plotBackgroundColor: null,
      plotBorderWidth: null,
      plotShadow: false,
      marginLeft: 50,
      marginRight: 50,
      marginBottom: 50,
    },
    title: { text: ' ' },
    tooltip: {
      headerFormat: '{point.key}<br>Market Value: <b>{point.y}</b>',
      pointFormat: '<br>Allocation: <b>{point.percentage:.1f}%</b>',
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        size: '80%',
        innerSize: '50%',
        borderWidth: 0,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '<span style="color:{point.color};">{point.name}</span>',
          style: {
            color:
              (Highcharts.theme && Highcharts.theme.contrastTextColor) ||
              'black',
            fontSize: REGULAR_FONT_SIZE,
            shadow: false,
            textShadow: 'none',
          },
        },
        point: {
          events: {
            mouseOver() {
              (this as any).slice(true);
            },
            mouseOut() {
              (this as any).slice(false);
            },
          },
        },
      },
    },
  };
}

export function defaultEarningsBarOptions(Highcharts) {
  return {
    chart: {
      type: 'column',
      marginTop: 30,
      marginBottom: 30,
    },
    tooltip: {
      formatter() {
        return (this as any).series.name + ':' + (this as any).y + '%';
      },
    },
    legend: {
      enabled: true,
    },
    xAxis: {
      //crossing:0,
      tickWidth: 0,
      lineWidth: 0,
      labels: {
        enabled: false,
      },
      categories: ['', ''],
    },
    yAxis: {
      tickWidth: 0,
      plotLines: [
        {
          color: '#CCC',
          width: 1,
          value: 0,
        },
      ],
      gridLineWidth: 0,
      title: {
        text: '',
      },
      labels: {
        enabled: false,
      },
      stackLabels: {
        enabled: true,
        //useHTML: true,
        verticalAlign: 'middle',
        Align: 'center',
        formatter() {
          if ((this as any).total !== 0) {
            return (this as any).total + '%';
          }
        },
      },
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        borderWidth: 0,
        pointPadding: 0,
        dataLabels: {
          enabled: false,
          color:
            (Highcharts.theme && Highcharts.theme.dataLabelsColor) || 'white',
          style: {
            fontFamily: '"Roboto"',
            fontWeight: '700',
            fontSize: '1.8em',
          },
          format: '{x}',
        },
      },
    },
  };
}

export function defaultPayoutOptions(Highcharts) {
  return {
    chart: {
      type: 'pie',
      marginBottom: 30,
      marginTop: 30,
      marginLeft: 30,
      marginRight: 30,
      spacingBottom: 10,
      spacingTop: 10,
      spacingLeft: 10,
      spacingRight: 10,
    },
    colors: ['#863232', '#863232'],
    title: {
      align: 'center',
      y: -6,
      verticalAlign: 'middle',
      style: {
        color: (Highcharts.theme && Highcharts.theme.textColor) || '#fffefc',
        fontWeight: 'bold',
        fontSize: '1.6em',
      },
    },
    tooltip: {
      valueSuffix: '%',
    },
    plotOptions: {
      pie: {
        size: '80%',
        innerSize: '50%',
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          format: '{point.name}',
          style: {
            color:
              (Highcharts.theme && Highcharts.theme.contrastTextColor) ||
              'white',
          },
        },
      },
    },
  };
}

export function formatYAxis(value, symbol) {
  let valueSymbol = [value, symbol];
  if (symbol === 'M' && Math.abs(value) >= 1000) {
    valueSymbol = formatYAxis(value / 1000, 'B');
  } else if (symbol === 'B' && Math.abs(value) >= 1000) {
    valueSymbol = formatYAxis(value / 1000, 'T');
  }
  return valueSymbol;
}
